import { useState } from "react";
import { useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight,
  faEye,
  faFilePdf,
  faPalette,
} from "@fortawesome/free-solid-svg-icons";
import { cn } from "../../../../../components/utils/cn";
import { useStatsigClient } from "../../../../../hooks";

interface Props {
  linkId: string;
}

export const DefaultLeadActions = ({ linkId }: Props) => {
  const [isLoadingPdf, setIsLoadingPdf] = useState<boolean>(false);
  const { organizationId } = useParams<{ organizationId?: string }>();
  const statsigClient = useStatsigClient();
  // Internal-only: the template editor entry is hidden behind a feature gate.
  const showTemplateEditor = statsigClient?.checkGate("cv_template_editor");

  const link = `${window.location.origin}/p/`;

  const previewPdf = async () => {
    setIsLoadingPdf(true);
    var pdfUrl = "https://profile-pdf-exporter.onrender.com";
    var mainUrl = "https://voc.studio";

    if (linkId) {
      const downloadUrl =
        pdfUrl + `/render?url=${mainUrl}/pdf/${linkId}&inline=true`;

      try {
        const response = await fetch(downloadUrl, {
          headers: {
            x_api_key: process.env.REACT_APP_X_API_KEY ?? "",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch PDF");
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, "_blank");

        // Clean up the blob URL after a delay
        setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
      } catch (error) {
        console.error("Error fetching PDF:", error);
        alert("Failed to open PDF preview");
      } finally {
        setIsLoadingPdf(false);
      }
    }
  };

  return (
    <>
      <div className={"transition-all duration-300 ease-in-out"}>
        <div
          className="py-4 px-2 flex flex-row gap-2 items-center justify-between flex-1 min-w-0 cursor-pointer rounded-2xl lg:hover:bg-gray-2 active:lg:bg-gray-3 active:scale-95 transition-all duration-200"
          onClick={async () => await previewPdf()}
        >
          <div className="flex items-center gap-2">
            <FontAwesomeIcon
              icon={faFilePdf}
              className="w-3 h-3 text-black flex-shrink-0"
            />
            <div className="flex flex-col text-left">
              <p
                className={cn(
                  "text-xs font-black",
                  isLoadingPdf ? "text-gray-6" : "text-black",
                )}
              >
                {isLoadingPdf ? "Loading PDF..." : "Preview PDF"}
              </p>
            </div>
          </div>
          <FontAwesomeIcon
            icon={faChevronRight}
            className="w-3 h-3 text-black flex-shrink-0"
          />
        </div>
      </div>
      <div className={"transition-all duration-300 ease-in-out"}>
        <div
          className="py-4 px-2 flex flex-row gap-2 items-center justify-between flex-1 min-w-0 cursor-pointer rounded-2xl lg:hover:bg-gray-2 active:lg:bg-gray-3 active:scale-95 transition-all duration-200"
          onClick={() => {
            window.open(link + `${linkId}`, "_blank");
          }}
        >
          <div className="flex items-center gap-2">
            <FontAwesomeIcon
              icon={faEye}
              className="w-3 h-3 text-black flex-shrink-0"
            />
            <div className="flex flex-col text-left">
              <p className={"text-xs font-black text-black"}>Preview Link</p>
            </div>
          </div>
          <FontAwesomeIcon
            icon={faChevronRight}
            className="w-3 h-3 text-black flex-shrink-0"
          />
        </div>
      </div>
      {/* Internal-only (behind the cv_template_editor gate): opens the
          experimental template editor for this profile version. */}
      {showTemplateEditor && organizationId && linkId && (
        <div className={"transition-all duration-300 ease-in-out"}>
          <div
            className="py-4 px-2 flex flex-row gap-2 items-center justify-between flex-1 min-w-0 cursor-pointer rounded-2xl lg:hover:bg-gray-2 active:lg:bg-gray-3 active:scale-95 transition-all duration-200"
            onClick={() => {
              window.open(
                `/dash/${organizationId}/templates/${linkId}`,
                "_blank",
              );
            }}
          >
            <div className="flex items-center gap-2">
              <FontAwesomeIcon
                icon={faPalette}
                className="w-3 h-3 text-black flex-shrink-0"
              />
              <div className="flex flex-col text-left">
                <p className={"text-xs font-black text-black"}>
                  Template editor (beta)
                </p>
              </div>
            </div>
            <FontAwesomeIcon
              icon={faChevronRight}
              className="w-3 h-3 text-black flex-shrink-0"
            />
          </div>
        </div>
      )}
    </>
  );
};
